/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { inject, Injectable, NgZone } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { take } from 'rxjs/operators';
import { USER_IS_LOGGED_IN } from '../../../user-auth/services/api-basic-auth.service';

interface CookieControlPlugin {
  load: (config: unknown) => void;
  open?: () => void;
  hide?: () => void;
}

declare global {
  interface Window {
    CookieControl?: CookieControlPlugin;
  }
}

export const NECESSARY_COOKIES = [
  'CookieControl',
  'FolioAccessToken',
  'redirectAfterLogin',
  'authorised_client',
  'f_authorised_client',
  'AUTH_SESSION_ID',
  'KEYCLOAK_IDENTITY',
  'KEYCLOAK_SESSION',
  'KEYCLOAK_LOCALE',
  'KC_RESTART',
];

export const ANALYTICS_COOKIES = ['_ga', '_gid', '_gat', '_gcl_au', '__utma', '__utmb', '__utmc', '__utmt', '__utmz'];

export const MARKETING_COOKIES = [
  'drift_aid',
  'driftt_aid',
  'drift_campaign_refresh',
  '_fbp',
  'fr',
  '_uetsid',
  '_uetvid',
  'li_sugr',
  'lidc',
];

const COOKIE_POLICY_URL = 'https://www.potential.ly/privacy#cookies';
const POLICY_UPDATED = '17/09/2026';
const PLUGIN_POLL_INTERVAL_MS = 250;
const PLUGIN_POLL_ATTEMPTS = 40;

@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  private readonly transloco = inject(TranslocoService);
  private readonly ngZone = inject(NgZone);

  private loadRequested = false;
  private pluginLoaded = false;

  load(): void {
    if (this.loadRequested) {
      return;
    }
    this.loadRequested = true;
    this.transloco
      .load(this.transloco.getActiveLang())
      .pipe(take(1))
      .subscribe({
        next: () => this.awaitPlugin(0),
        error: () => this.awaitPlugin(0),
      });
  }

  open(): void {
    window.CookieControl?.open?.();
  }

  get available(): boolean {
    return this.pluginLoaded && typeof window.CookieControl?.open === 'function';
  }

  private awaitPlugin(attempt: number): void {
    if (window.CookieControl) {
      this.ngZone.run(() => {
        window.CookieControl.load(this.buildConfig());
        this.pluginLoaded = true;
      });
      return;
    }
    if (attempt >= PLUGIN_POLL_ATTEMPTS) {
      return;
    }
    this.ngZone.runOutsideAngular(() => setTimeout(() => this.awaitPlugin(attempt + 1), PLUGIN_POLL_INTERVAL_MS));
  }

  private buildConfig(): unknown {
    const t = (key: string) => this.transloco.translate(`translations.cookieConsent.${key}`);
    const isSafari = this.isSafari();

    return {
      apiKey: '7d639679919fe751bb087c529e486d9b761df8b6',
      product: 'PRO',
      initialState: 'notify',
      mode: 'gdpr',
      acceptBehaviour: 'all',
      notifyDismissButton: false,
      rejectButton: true,
      closeOnGlobalChange: true,
      consentCookieExpiry: isSafari ? 900 : 90,
      subDomains: true,
      sameSiteCookie: true,
      sameSiteValue: 'Lax',
      branding: {
        removeIcon: this.userIsSignedIn(),
      },
      accessibility: {
        outline: true,
        highlightFocus: true,
      },
      statement: {
        name: t('statementName'),
        description: t('statementDescription'),
        url: COOKIE_POLICY_URL,
        updated: POLICY_UPDATED,
      },
      necessaryCookies: NECESSARY_COOKIES,
      optionalCookies: [
        {
          name: 'analytics',
          label: t('analytics.label'),
          description: t('analytics.description'),
          cookies: ANALYTICS_COOKIES,
          recommendedState: false,
          initialConsentState: false,
          thirdPartyCookies: [{ name: 'Google Analytics', optOutLink: 'https://tools.google.com/dlpage/gaoptout' }],
        },
        {
          name: 'marketing',
          label: t('marketing.label'),
          description: t('marketing.description'),
          cookies: MARKETING_COOKIES,
          recommendedState: false,
          initialConsentState: false,
        },
      ],
      text: {
        title: t('title'),
        intro: t('intro'),
        necessaryTitle: t('necessaryTitle'),
        necessaryDescription: t('necessaryDescription'),
        thirdPartyTitle: t('thirdPartyTitle'),
        thirdPartyDescription: t('thirdPartyDescription'),
        thirdPartyCookies: t('thirdPartyCookies'),
        notifyTitle: t('notifyTitle'),
        notifyDescription: t('notifyDescription'),
        on: t('on'),
        off: t('off'),
        accept: t('accept'),
        reject: t('reject'),
        settings: t('settings'),
        acceptRecommended: t('acceptRecommended'),
        acceptSettings: t('accept'),
        rejectSettings: t('reject'),
        readMore: t('readMore'),
      },
    };
  }

  private userIsSignedIn(): boolean {
    try {
      return localStorage.getItem(USER_IS_LOGGED_IN) === 'true';
    } catch {
      return false;
    }
  }

  private isSafari(): boolean {
    const ua = navigator.userAgent.toLowerCase();
    return ua.includes('safari') && !ua.includes('chrome');
  }
}
